import { StatCard } from '@/components/ui/stat-card';
import { SkeletonText } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Varied label/value widths. Real stat rows never have uniform text, and a grid
 * of identical bars reads as a placeholder graphic rather than as content.
 */
const LABEL_WIDTHS = ['62%', '78%', '54%', '70%', '84%', '58%'] as const;
const VALUE_WIDTHS = ['44%', '32%', '52%', '38%', '48%', '34%'] as const;

interface StatCardSkeletonProps {
    readonly count?: number;
    /**
     * Responsive grid classes for the row. Defaults to the 6-up layout the
     * analytics screens use; pass the page's own classes when they differ so
     * the cards wrap at the same breakpoints as the real ones.
     */
    readonly gridClassName?: string;
}

/**
 * Loading placeholder for a row of `StatCard`s.
 *
 * Composes the real `StatCard` rather than re-describing its border, radius and
 * padding, so card dimensions are identical to the loaded state by construction
 * — the only thing that changes on swap is the text inside.
 *
 * `StatLabel` is `text-xs` with `mb-1`; `StatValue` is `text-2xl`. Both are
 * reproduced at their true line boxes so the row height never shifts.
 */
export function StatCardSkeleton({
    count = 4,
    gridClassName = 'grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6',
}: StatCardSkeletonProps) {
    return (
        <div className={cn(gridClassName)}>
            {Array.from({ length: count }).map((_, i) => (
                <StatCard key={i}>
                    <SkeletonText
                        size="xs"
                        width={LABEL_WIDTHS[i % LABEL_WIDTHS.length]}
                        className="mb-1"
                    />
                    <SkeletonText size="h1" width={VALUE_WIDTHS[i % VALUE_WIDTHS.length]} />
                </StatCard>
            ))}
        </div>
    );
}
